import type { AxiosResponse } from 'axios'
import apiClient from '../client'
import { API_ENDPOINTS } from '@/config/api'
import { handleApiError } from '@/utils/apiErrorHandling'
import authService from './authService'
import {
  RespondersResponse,
  NotifyResponderResponse,
  NotifyResponderRequest,
  ResponderListParams,
  EmergencyCase,
  ResponderDetail,
  ResponderDetailResponse,
} from '@/types/api/responders'

class ResponderService {
  /**
   * Get all responders
   */
  async getAllResponders(
    params: ResponderListParams
  ): Promise<RespondersResponse['data']> {
    try {
      const precinctInfo = await authService.getPrecinct()

      const queryParams: Record<string, string | number> = {
        entity: 'responders',
        skip_cache: 'true',
        task: 'trim',
        ...params,
      }

      if (precinctInfo?.precinct_id) {
        queryParams.precinct_id = precinctInfo.precinct_id
      }

      const response: AxiosResponse<any> = await apiClient.get(
        API_ENDPOINTS.ENTITIES,
        {
          params: queryParams,
          validateStatus: () => true,
        }
      )

      if (response.status !== 200) {
        throw new Error('Failed to fetch responders')
      }

      // Ensure we have an array for items - the actual data is nested deeper
      const itemsArray = Array.isArray(response.data.data.data)
        ? response.data.data.data
        : []

      // Transform the actual API response to match the expected PaginatedResponse structure
      const transformedData: RespondersResponse['data'] = {
        items: itemsArray,
        pagination: {
          currentPage: params.pageNumber,
          totalPages: Math.ceil(
            (response.data.data.total_count || 0) / params.numberPerPage
          ),
          totalItems: response.data.data.total_count || 0,
          itemsPerPage: params.numberPerPage,
          hasNextPage: response.data.data.last_key ? true : false,
          hasPreviousPage: params.pageNumber > 1,
        },
      }

      return transformedData
    } catch (error) {
      throw handleApiError(error, 'getAllResponders')
    }
  }

  /**
   * Notify responders
   */
  async notifyResponder(
    data: NotifyResponderRequest
  ): Promise<NotifyResponderResponse['data']> {
    try {
      const response: AxiosResponse<NotifyResponderResponse> =
        await apiClient.post(
          `${API_ENDPOINTS.DISPATCHERS}/notify-responders`,
          data
        )

      return response.data.data
    } catch (error) {
      throw handleApiError(error, 'notifyResponder')
    }
  }

  /**
   * Get a single responder by ID
   */
  async getResponderById(responderId: string): Promise<ResponderDetail> {
    try {
      const response: AxiosResponse<ResponderDetailResponse> =
        await apiClient.get(API_ENDPOINTS.RESPONDER_BY_ID(responderId))
      return response.data.data
    } catch (error) {
      throw handleApiError(error, 'getResponderById')
    }
  }

  /**
   * Get emergency case assignments for a responder
   */
  async getResponderEmergencyCases(
    responderId: string,
    numberPerPage: number = 100,
    caseStatus?: string
  ): Promise<EmergencyCase[]> {
    try {
      const queryParams: Record<string, any> = {
        entity: 'cases',
        task: 'all',
        skip_cache: true,
        req_source: 'responder',
        numberPerPage,
        responder_id: responderId,
      }

      // Only add case_status if it's provided
      if (caseStatus) {
        queryParams.case_status = caseStatus
      }

      const response: AxiosResponse<any> = await apiClient.get(
        API_ENDPOINTS.ENTITIES,
        { params: queryParams }
      )

      // Extract from response.data.data.data
      const cases = response.data.data.cases || []
      return cases
    } catch (error) {
      console.error('Error fetching emergency cases:', error)
      throw handleApiError(error, 'getResponderEmergencyCases')
    }
  }
}

// Export singleton instance
export const responderService = new ResponderService()
export default responderService
